import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// The shopping list renders each item inside animated (transformed) cards that sit
// in aisle groups with `overflow: hidden` / `contain: paint`. A normally positioned
// dropdown menu is therefore painted behind later cards and clipped by the group,
// which cut off the unit options. Rendering the menu in a portal on <body> with
// fixed positioning sidesteps every one of those stacking/clipping contexts.

const MENU_MAX_HEIGHT = 200;
const MENU_MIN_HEIGHT = 80;
const VIEWPORT_PADDING = 8;
const MENU_GAP = 2;

const SetUnitDropdown = ({ options = [], isActive = false, onSelectDefault, onSelectOption }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);

  const updatePosition = useCallback(() => {
    const toggle = toggleRef.current;
    if (!toggle) return;

    const rect = toggle.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_PADDING;
    const spaceAbove = rect.top - MENU_GAP - VIEWPORT_PADDING;
    const dropUp = spaceBelow < MENU_MIN_HEIGHT && spaceAbove > spaceBelow;
    const maxHeight = Math.max(
      MENU_MIN_HEIGHT,
      Math.min(MENU_MAX_HEIGHT, dropUp ? spaceAbove : spaceBelow)
    );

    const menuWidth = Math.max(menuRef.current ? menuRef.current.offsetWidth : 0, rect.width);
    // align="end": the menu's right edge lines up with the toggle's right edge
    let left = rect.right - menuWidth;
    left = Math.min(left, window.innerWidth - VIEWPORT_PADDING - menuWidth);
    left = Math.max(VIEWPORT_PADDING, left);

    setMenuStyle({
      position: "fixed",
      left,
      top: dropUp ? undefined : rect.bottom + MENU_GAP,
      bottom: dropUp ? window.innerHeight - rect.top + MENU_GAP : undefined,
      minWidth: rect.width,
      maxHeight,
    });
  }, []);

  // Position before the first paint so the menu never flashes in the wrong spot.
  useLayoutEffect(() => {
    if (open) updatePosition();
    else setMenuStyle(null);
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (
        (toggleRef.current && toggleRef.current.contains(event.target)) ||
        (menuRef.current && menuRef.current.contains(event.target))
      ) {
        return;
      }
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    // Follow the toggle while the page or any scroll container moves.
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  const choose = (handler) => () => {
    setOpen(false);
    if (handler) handler();
  };

  return (
    <div className="btn-group set-unit-dropdown">
      <button
        ref={toggleRef}
        type="button"
        className={`btn btn-sm dropdown-toggle dropdown-toggle-2 ${
          isActive ? "btn-success" : "btn-secondary"
        }`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        Set Unit
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="dropdown-menu show set-unit-dropdown-menu"
            style={{ ...(menuStyle || { position: "fixed", top: -9999, left: -9999 }) }}
          >
            <button type="button" className="dropdown-item" onClick={choose(onSelectDefault)}>
              <span>Default</span>
            </button>
            {options.map((option, index) => (
              <button
                key={option.id ?? index}
                type="button"
                className="dropdown-item"
                onClick={choose(() => onSelectOption(option.id))}
              >
                <span>{option.unit_name}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default SetUnitDropdown;
